import JSZip from "jszip";

import { getDocumentFileToBase64 } from "../document";
import { wlog } from "./log";

export const ICM_AGREEMENT_NAMESPACE = "ICMAgreementNamespace";
// Word persists the xmlns attribute with double quotes (the ICI writer authors single
// quotes; Word normalizes on save), so the zip fallback matches this literal.
const CUSTOM_XML_PART_MARKER = "ICMAgreementNamespace xmlns=\"ICMAgreementNamespace\"";
const CUSTOM_XML_ZIP_DIR = "customxml/";
const SUPPORTED_TYPES = ["Agreement", "AssociatedDocument"];
const WOPI_PIPE_MIN_PARTS = 4;

export interface AgreementIdentity {
    instanceId: string;
    entityName: string;
    isAssociatedDocument: boolean;
}

interface RawIdentity {
    instanceId: string;
    entityName: string;
    type: string;
}

function elementChildren(parent: Element): Element[] {
    return Array.from(parent.children);
}

function childText(parent: Element, localName: string): string {
    const child = elementChildren(parent).find((candidate) => candidate.localName === localName);
    return child?.textContent?.trim() ?? "";
}

// WOPI shape: the platform stores each identity in a <meta> child whose tag name
// contains "_", holding "ContractTypeName|SysId|Type|Version". A document shared
// across agreements carries one such child per referencing agreement.
function parseWopiPipeShapes(meta: Element): RawIdentity[] {
    return elementChildren(meta)
        .filter((candidate) => candidate.tagName.includes("_"))
        .map((candidate) => (candidate.textContent ?? "").split("|"))
        .filter((parts) => parts.length >= WOPI_PIPE_MIN_PARTS)
        .map((parts) => ({ entityName: parts[0], instanceId: parts[1], type: parts[2] }));
}

// The shape the ICI writer authors directly: SysId / Type / EntityName element children.
function parseElementChildrenShape(meta: Element): RawIdentity | null {
    const instanceId = childText(meta, "SysId");
    if (!instanceId) return null;
    return { instanceId, entityName: childText(meta, "EntityName"), type: childText(meta, "Type") };
}

function toIdentity(raw: RawIdentity): AgreementIdentity {
    if (!raw.instanceId || !raw.entityName) {
        throw new Error("[wopi] agreement identity is missing its instance id or entity name");
    }
    return {
        instanceId: raw.instanceId,
        entityName: raw.entityName,
        isAssociatedDocument: raw.type === "AssociatedDocument",
    };
}

// Pure. Throws on malformed XML, missing <meta>, no recognizable shape, or when
// no candidate has a supported Type.
export function parseAgreementIdentityCandidates(xml: string): AgreementIdentity[] {
    const doc = new DOMParser().parseFromString(xml, "text/xml");
    if (doc.getElementsByTagName("parsererror").length > 0) {
        throw new Error("[wopi] agreement identity XML is malformed");
    }
    const meta = doc.getElementsByTagNameNS("*", "meta")[0];
    if (!meta) {
        throw new Error("[wopi] agreement identity XML has no <meta> element");
    }
    const raws = parseWopiPipeShapes(meta);
    const elementShape = parseElementChildrenShape(meta);
    if (elementShape) raws.push(elementShape);
    if (raws.length === 0) {
        throw new Error("[wopi] agreement identity XML has no recognizable identity shape");
    }
    const supported = raws.filter((raw) => SUPPORTED_TYPES.includes(raw.type));
    if (supported.length === 0) {
        throw new Error(
            `[wopi] unsupported document type "${raws[0].type}" — only Agreement and AssociatedDocument are supported`
        );
    }
    return supported.map(toIdentity);
}

function officeAsync<T>(
    operation: string,
    run: (callback: (result: Office.AsyncResult<T>) => void) => void
): Promise<T> {
    return new Promise((resolve, reject) => {
        run((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve(result.value);
            } else {
                reject(new Error(`[wopi] ${operation} failed: ${result.error.message}`));
            }
        });
    });
}

async function readRegisteredPartXml(): Promise<string | null> {
    const parts = await officeAsync<Office.CustomXmlPart[]>("getByNamespaceAsync", (callback) =>
        Office.context.document.customXmlParts.getByNamespaceAsync(ICM_AGREEMENT_NAMESPACE, callback)
    );
    if (parts.length === 0) return null;
    const nodes = await officeAsync<Office.CustomXmlNode[]>("getNodesAsync", (callback) =>
        parts[0].getNodesAsync("/", callback)
    );
    return officeAsync<string>("getNodeValueAsync", (callback) => nodes[0].getNodeValueAsync(callback));
}

// Word Online often doesn't register embedded custom XML parts, so read the whole
// docx, unzip it, and pull the part's text straight out of the customXml/ entries.
async function readEmbeddedPartXml(): Promise<string | null> {
    const base64 = await getDocumentFileToBase64();
    wlog(`agreement identity: zip fallback reading document (base64 len=${base64.length})`);
    const zip = await new JSZip().loadAsync(base64, { base64: true, checkCRC32: false });
    const candidatePaths = Object.keys(zip.files).filter((path) => path.toLowerCase().includes(CUSTOM_XML_ZIP_DIR));
    for (const path of candidatePaths) {
        const text = await zip.files[path].async("string");
        if (text.includes(CUSTOM_XML_PART_MARKER)) {
            wlog(`agreement identity: zip fallback matched ${path}`);
            return text;
        }
    }
    return null;
}

export async function readAgreementIdentityPartXml(): Promise<string | null> {
    const registeredXml = await readRegisteredPartXml();
    if (registeredXml != null) {
        wlog("agreement identity: part text read from the registered custom XML part");
        return registeredXml;
    }
    wlog("agreement identity: no registered custom XML part → zip fallback");
    const embeddedXml = await readEmbeddedPartXml();
    if (embeddedXml == null) {
        wlog("agreement identity: document carries no ICMAgreementNamespace part");
    }
    return embeddedXml;
}
